// src/SparepartsPages/Dpo/DpoInsert.js (FULL FRONTEND) ✅ minimal change: just messages/status text aligned to new backend
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  TextField,
  Stack,
  Divider,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Chip,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Autocomplete from "@mui/material/Autocomplete";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ------------ helpers ------------ */
const clean = (v) => (v === null || v === undefined ? "" : String(v)).trim();
const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const pick = (obj, keys) => {
  const lower = Object.keys(obj).reduce((acc, k) => {
    acc[k.toLowerCase().trim()] = k;
    return acc;
  }, {});
  for (const k of keys) {
    const real = lower[k.toLowerCase().trim()];
    if (real) return obj[real];
  }
  return "";
};
const excelNumberToISO = (n, date1904) => {
  const o = XLSX.SSF.parse_date_code(n, { date1904 });
  if (!o || !o.y || !o.m || !o.d) return "";
  return `${o.y}-${pad2(o.m)}-${pad2(o.d)}`;
};
const normalizeAnyDateToISO = (v, date1904 = false) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) return excelNumberToISO(v, date1904);
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (m) return `${m[3]}-${pad2(Number(m[2]))}-${pad2(Number(m[1]))}`;
  return s;
};
const toNum = (v) => {
  const x = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(x) ? x : 0;
};
const pickCI = (row, keys) => {
  if (!row) return "";
  const map = {};
  Object.keys(row).forEach((k) => (map[String(k).trim().toLowerCase()] = row[k]));
  for (const k of keys) {
    const v = map[String(k).trim().toLowerCase()];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const DpoInsert = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const isLoggedIn = !!userToken;

  useEffect(() => {
    const t = setTimeout(() => window.location.reload(false), 1800000);
    return () => clearTimeout(t);
  }, []);

  const [tab, setTab] = useState(0);

  // party autocomplete (upload + list filter)
  const [partyOpen, setPartyOpen] = useState(false);
  const [partyLoading, setPartyLoading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyQ, setPartyQ] = useState("");
  const [party, setParty] = useState(null);

  // DPO upload
  const [fileName, setFileName] = useState("");
  const [uploadRows, setUploadRows] = useState([]);
  const [uploading, setUploading] = useState(false);

  // InvNo map upload
  const [invFileName, setInvFileName] = useState("");
  const [invRows, setInvRows] = useState([]);
  const [invUploading, setInvUploading] = useState(false);

  // list filters (All/Pending)
  const [filters, setFilters] = useState({ dpono: "", item: "", partyid: "", from: "", to: "" });
  const [listLoading, setListLoading] = useState(false);
  const [listRows, setListRows] = useState([]);
  const [total, setTotal] = useState(0);

  const tfSmall = { size: "small", fullWidth: true };
  const btnSmall = { size: "small" };
  const chipSmall = { size: "small" };

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: { sptoken: userToken, ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Request failed");
    return data;
  };

  /* ---------------- party load ---------------- */
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!isLoggedIn || !partyOpen) return;
      setPartyLoading(true);
      try {
        const data = await fetchJSON(`/dpo/partylist?q=${encodeURIComponent(partyQ)}`);
        if (alive) setPartyOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(e.message);
      } finally {
        if (alive) setPartyLoading(false);
      }
    };
    run();
    return () => (alive = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyOpen, partyQ, isLoggedIn]);

  /* ---------------- template downloads ---------------- */
  const downloadTemplate = async () => {
    try {
      const res = await fetch(`/dpo/template`, { method: "GET", headers: { sptoken: userToken } });
      if (!res.ok) throw new Error("Template download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "DPO_Template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  /* ---------------- excel parse (DPO upload) ---------------- */
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);

    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: false });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const date1904 = !!wb?.Workbook?.WBProps?.date1904;

      const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });
      const mapped = json.map((r, i) => {
        const dpodateRaw = pick(r, ["dpodate", "dpo date"]);
        const dpodate = normalizeAnyDateToISO(dpodateRaw, date1904);

        return {
          id: i + 1,
          dpono: clean(pick(r, ["dpono", "dpo no", "dpno"])),
          dpodate,
          itemcode: clean(pick(r, ["itemcode", "item code"])),
          itemname: clean(pick(r, ["itemname", "item name"])),
          qty: Number(String(pick(r, ["qty", "quantity"])).replace(/,/g, "").trim()) || 0,
          status: "pending",
          message: "",
          stockqty: 0,
          stockShort: false,
          action: "",
        };
      });

      if (!mapped.length) return toast.error("No rows found in Excel");
      setUploadRows(mapped);
      toast.success(`Loaded ${mapped.length} rows`);
    } catch (err) {
      toast.error(err.message || "Failed to read Excel");
    } finally {
      e.target.value = "";
    }
  };

  /* ---------------- upload DPO ---------------- */
  const uploadToServer = async () => {
    if (!party?.subledgerid) return toast.error("Select Party first");
    if (!uploadRows.length) return toast.error("Upload Excel first");

    setUploading(true);
    try {
      const payload = {
        subledgerid: party.subledgerid,
        rows: uploadRows.map((r) => ({
          dpono: r.dpono,
          dpodate: r.dpodate,
          itemcode: r.itemcode,
          itemname: r.itemname,
          qty: r.qty,
        })),
      };

      const data = await fetchJSON(`/dpo/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rr = Array.isArray(data.rowResults) ? data.rowResults : [];
      const mapByIdx = new Map(rr.map((x) => [Number(x.idx), x]));

      const merged = uploadRows.map((r, idx) => {
        const x = mapByIdx.get(idx);
        if (!x) return r;
        return {
          ...r,
          status: x.status,
          message: x.message || "",
          stockqty: x.stockqty ?? 0,
          stockShort: !!x.stockShort,
          action: x.action || "",
          dpodate: normalizeAnyDateToISO(x.dpodate || r.dpodate),
        };
      });

      setUploadRows(merged);
      toast.success(data.message || "Done");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  /* ---------------- excel parse (InvNo Map upload) ---------------- */
  const onPickInvnoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setInvFileName(f.name);

    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: false });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });

      const mapped = json
        .map((r, i) => {
          const dpono = clean(
            pickCI(r, ["dealer order no", "tvs order no", "order no", "dpono", "dpo no", "dpno", "dpo"])
          );
          const invno = clean(pickCI(r, ["invoice no", "invoice number", "invno", "inv no", "invoice"]));
          const itemcode = clean(pickCI(r, ["itemcode", "item code", "part code", "partcode", "item_code"]));
          const invqty = toNum(pickCI(r, ["invoice qty", "invoice quantity", "invqty", "invoice_qty","Supplier Invoice Qty"]));

          const invqtyValid = Number(invqty) > 0;

          return {
            id: i + 1,
            idx: i,
            dpono,
            invno,
            itemcode,
            invqty,
            status: invqtyValid ? "pending" : "invalid",
            message: invqtyValid ? "" : "InvQty missing/0 (must be > 0)",
          };
        })
        .filter((x) => x.dpono || x.invno || x.itemcode);

      if (!mapped.length) return toast.error("No rows found in Excel");
      setInvRows(mapped);
      toast.success(`Loaded ${mapped.length} rows`);
    } catch (err) {
      toast.error(err.message || "Failed to read Excel");
    } finally {
      e.target.value = "";
    }
  };

  /* ---------------- upload InvNo Map ---------------- */
  const uploadInvnoToServer = async () => {
    if (!invRows.length) return toast.error("Upload Excel first");

    const validRows = invRows.filter(
      (r) => clean(r.dpono) && clean(r.invno) && clean(r.itemcode) && Number(r.invqty) > 0
    );
    if (!validRows.length) return toast.error("No valid rows (need DPO + Invoice + Itemcode + InvQty > 0)");

    setInvUploading(true);
    try {
      const payload = {
        rows: validRows.map((r) => ({
          dpono: r.dpono,
          invno: r.invno,
          invqty: Number(r.invqty),
          itemcode: r.itemcode,
        })),
      };

      const data = await fetchJSON(`/dpo/invno/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rr = Array.isArray(data.rowResults) ? data.rowResults : [];
      const mapByIdx = new Map(rr.map((x) => [Number(x.idx), x]));

      const merged = invRows.map((r, i) => {
        const x = mapByIdx.get(i);
        if (!x) return r;
        return { ...r, status: x.status || r.status, message: x.message || r.message };
      });

      setInvRows(merged);
      toast.success(data.message || "Invoice map saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setInvUploading(false);
    }
  };

  /* ---------------- list fetch ---------------- */
  const loadList = async () => {
    setListLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("invnull", tab === 3 ? "1" : "0");
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) qs.set(k, String(v));
      });
      qs.set("limit", "200");
      qs.set("offset", "0");

      const data = await fetchJSON(`/dpo/list?${qs.toString()}`);
      const rows = (data.rows || []).map((r) => ({
        ...r,
        id: r.dpoid,
        dpodate: normalizeAnyDateToISO(r.dpodate),
      }));

      setListRows(rows);
      setTotal(Number(data.total || 0));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    if (tab === 2 || tab === 3) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isLoggedIn]);

  const listStats = useMemo(() => {
    const low = listRows.filter((r) => r.stock_short).length;
    const conflict = listRows.filter((r) => r.party_conflict).length;
    const unmapped = listRows.filter((r) => Number(r.invoice_count || 0) === 0).length;
    return { low, conflict, unmapped };
  }, [listRows]);

  /* ---------------- grids ---------------- */
  const uploadCols = [
    { field: "dpono", headerName: "DPO", flex: 0.8, minWidth: 110 },
    { field: "dpodate", headerName: "Date", flex: 0.7, minWidth: 95 },
    { field: "itemcode", headerName: "Item", flex: 0.8, minWidth: 110 },
    { field: "itemname", headerName: "Name", flex: 2, minWidth: 220 },
    { field: "qty", headerName: "Qty", flex: 0.5, minWidth: 70, type: "number" },
    {
      field: "status",
      headerName: "Status",
      flex: 1.7,
      minWidth: 380,
      renderCell: (p) => {
        const s = p.value;
        const action = p.row.action;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {s === "ok" && <Chip size="small" label={action ? action.toUpperCase() : "OK"} color="success" />}
            {s === "invalid" && <Chip size="small" label="INVALID" color="error" />}
            {s === "conflict" && <Chip size="small" label="PARTY CONFLICT" color="error" />}
            {s === "pending" && <Chip size="small" label="PENDING" />}
            {!!p.row.stockShort && <Chip size="small" label={`LOW STOCK (${p.row.stockqty})`} color="warning" />}
            {!!p.row.message && <Chip size="small" label={p.row.message} variant="outlined" />}
          </Stack>
        );
      },
    },
  ];

  const invCols = [
    { field: "dpono", headerName: "DPO", flex: 1, minWidth: 180 },
    { field: "invno", headerName: "InvNo", flex: 1, minWidth: 160 },
    { field: "itemcode", headerName: "Itemcode", flex: 0.8, minWidth: 130 },
    { field: "invqty", headerName: "InvQty", flex: 0.6, minWidth: 90, type: "number" },
    {
      field: "status",
      headerName: "Status",
      flex: 1.8,
      minWidth: 420,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {p.value === "ok" && <Chip size="small" label="OK (DPO exists)" color="success" />}
          {p.value === "stored" && <Chip size="small" label="STORED (DPO not uploaded)" color="warning" />}
          {p.value === "invalid" && <Chip size="small" label="INVALID" color="error" />}
          {p.value === "conflict" && <Chip size="small" label="PARTY CONFLICT" color="error" />}
          {p.value === "missing" && <Chip size="small" label="MISSING ITEM" color="error" />}
          {!!p.row.message && <Chip size="small" label={p.row.message} variant="outlined" />}
        </Stack>
      ),
    },
  ];

  const listCols = [
    { field: "dpodate", headerName: "Date", flex: 0.7, minWidth: 95 },
    { field: "dpono", headerName: "DPO", flex: 0.8, minWidth: 250 },
    { field: "subledgername", headerName: "Party", flex: 1.2, minWidth: 350 },
    { field: "itemcode", headerName: "Item", flex: 0.7, minWidth: 100 },
    { field: "itemname", headerName: "Name", flex: 2, minWidth: 220 },
    { field: "qty", headerName: "Qty", flex: 0.5, minWidth: 70, type: "number" },
    { field: "invqty", headerName: "InvQty", flex: 0.6, minWidth: 90, type: "number" },
    {
      field: "invoice_count",
      headerName: "Inv#",
      flex: 0.5,
      minWidth: 70,
      type: "number",
      renderCell: (p) => {
        const c = Number(p.value || 0);
        return c > 0 ? <Chip size="small" label={c} color="success" /> : <Chip size="small" label="0" />;
      },
    },
    {
      field: "invoices",
      headerName: "Invoices",
      flex: 1.2,
      minWidth: 250,
      sortable: false,
      renderCell: (p) => {
        const s = clean(p.value);
        if (!s) return <Chip size="small" label="UNMAPPED" />;
        return (
          <Tooltip title={s} arrow>
            <Box
              sx={{
                width: "100%",
                px: 1,
                py: 0.25,
                border: "1px solid",
                borderColor: "success.main",
                borderRadius: 1,
                color: "success.main",
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { height: 6 },
              }}
            >
              {s}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  if (!isLoggedIn) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2000} newestOnTop />
      <Sidebar
        Title={"DPO"}
        expandedPanel={"panel4"}
        children={
          <Box sx={{ p: 1 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                sx={{ py: 1, "& .MuiCardHeader-title": { fontSize: 14, fontWeight: 700 } }}
                title="DPO Upload & Invoice Map"
                subheader={
                  <Typography variant="caption" color="text.secondary">
                    Now you can upload <b>InvMap first</b> or <b>DPO first</b>. InvMap stores item+invqty inside
                    <b> t_dpo_invoice_map</b>. When DPO is uploaded, system auto-syncs <b>t_partywise_dpo.invqty</b>.
                  </Typography>
                }
              />
              <Divider />
              <CardContent sx={{ p: 1 }}>
                <Tabs
                  value={tab}
                  onChange={(e, v) => setTab(v)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 34,
                    "& .MuiTab-root": { minHeight: 34, py: 0.5, px: 1.5, fontSize: 12, textTransform: "none" },
                  }}
                >
                  <Tab label="Upload" />
                  <Tab label="InvNo Map" />
                  <Tab label="All" />
                  <Tab label="Pending" />
                </Tabs>

                <Divider sx={{ my: 1 }} />

                {/* TAB 0 - DPO Upload */}
                {tab === 0 && (
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          <Stack spacing={1}>
                            <Autocomplete
                              size="small"
                              open={partyOpen}
                              onOpen={() => setPartyOpen(true)}
                              onClose={() => setPartyOpen(false)}
                              options={partyOptions}
                              loading={partyLoading}
                              value={party}
                              onChange={(e, v) => setParty(v)}
                              getOptionLabel={(o) =>
                                o ? `${o.subledgername || ""} (${o.subledgercode || ""})` : ""
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Party"
                                  size="small"
                                  onChange={(e) => setPartyQ(e.target.value)}
                                />
                              )}
                            />

                            <Button {...btnSmall} variant="outlined" onClick={downloadTemplate}>
                              Download DPO Template
                            </Button>

                            <Button {...btnSmall} variant="contained" component="label" disabled={uploading}>
                              Pick Excel
                              <input type="file" hidden accept=".xlsx,.xls" onChange={onPickFile} />
                            </Button>

                            {!!fileName && (
                              <Typography variant="caption" color="text.secondary">
                                {fileName} • rows: {uploadRows.length}
                              </Typography>
                            )}

                            <Button
                              {...btnSmall}
                              variant="contained"
                              color="success"
                              onClick={uploadToServer}
                              disabled={uploading || !uploadRows.length || !party?.subledgerid}
                            >
                              {uploading ? <CircularProgress size={18} /> : "Upload to Server"}
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip {...chipSmall} label={`Rows: ${uploadRows.length}`} />
                            <Chip {...chipSmall} label={`Today: ${todayISO()}`} variant="outlined" />
                          </Stack>

                          <Box sx={{ height: 520, mt: 1 }}>
                            <DataGrid
                              rows={uploadRows}
                              columns={uploadCols}
                              density="compact"
                              rowHeight={30}
                              disableRowSelectionOnClick
                              slots={{ toolbar: GridToolbar }}
                              slotProps={{
                                toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } },
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* TAB 1 - InvNo Map Upload */}
                {tab === 1 && (
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          <Stack spacing={1}>
                            <Button {...btnSmall} variant="contained" component="label" disabled={invUploading}>
                              Pick Excel
                              <input type="file" hidden accept=".xlsx,.xls" onChange={onPickInvnoFile} />
                            </Button>

                            {!!invFileName && (
                              <Typography variant="caption" color="text.secondary">
                                {invFileName} • rows: {invRows.length}
                              </Typography>
                            )}

                            <Button
                              {...btnSmall}
                              variant="contained"
                              color="success"
                              onClick={uploadInvnoToServer}
                              disabled={invUploading || !invRows.length}
                            >
                              {invUploading ? <CircularProgress size={18} /> : "Upload Map"}
                            </Button>

                            <Typography variant="caption" color="text.secondary">
                              ✅ Required: <b>DPO</b>, <b>Invoice No</b>, <b>Itemcode</b>, <b>InvQty</b> (&gt; 0).
                              <br />
                              If DPO is not uploaded yet, rows are still stored in mapping table.
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          <Box sx={{ height: 520 }}>
                            <DataGrid
                              rows={invRows}
                              columns={invCols}
                              density="compact"
                              rowHeight={30}
                              disableRowSelectionOnClick
                              slots={{ toolbar: GridToolbar }}
                              slotProps={{
                                toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } },
                              }}
                              getRowClassName={(params) =>
                                !(Number(params.row.invqty) > 0) ? "row-error" : ""
                              }
                            />
                          </Box>

                          <style>{`.row-error { background: rgba(255,0,0,0.08); }`}</style>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* TAB 2 & 3 - All / Pending */}
                {(tab === 2 || tab === 3) && (
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          <Stack spacing={1}>
                            <TextField {...tfSmall} label="DPO contains" value={filters.dpono}
                              onChange={(e) => setFilters((p) => ({ ...p, dpono: e.target.value }))} />
                            <TextField {...tfSmall} label="Item contains" value={filters.item}
                              onChange={(e) => setFilters((p) => ({ ...p, item: e.target.value }))} />

                            <Autocomplete
                              size="small"
                              open={partyOpen}
                              onOpen={() => setPartyOpen(true)}
                              onClose={() => setPartyOpen(false)}
                              options={partyOptions}
                              loading={partyLoading}
                              value={partyOptions.find((x) => String(x.subledgerid) === String(filters.partyid)) || null}
                              onChange={(e, v) => setFilters((p) => ({ ...p, partyid: v?.subledgerid ? String(v.subledgerid) : "" }))}
                              getOptionLabel={(o) => (o ? `${o.subledgername || ""} (${o.subledgercode || ""})` : "")}
                              renderInput={(params) => (
                                <TextField {...params} {...tfSmall} label="Party" onChange={(e) => setPartyQ(e.target.value)} />
                              )}
                            />

                            <TextField {...tfSmall} label="From" type="date" InputLabelProps={{ shrink: true }}
                              value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} />
                            <TextField {...tfSmall} label="To" type="date" InputLabelProps={{ shrink: true }}
                              value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} />

                            <Button {...btnSmall} variant="contained" onClick={loadList} disabled={listLoading}>
                              {listLoading ? <CircularProgress size={18} /> : "Search"}
                            </Button>

                            <Button {...btnSmall} variant="text"
                              onClick={() => setFilters({ dpono: "", item: "", partyid: "", from: "", to: "" })}>
                              Reset
                            </Button>

                            <Divider />

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip {...chipSmall} label={`Total: ${total}`} />
                              <Chip {...chipSmall} label={`Unmapped: ${listStats.unmapped}`} />
                              <Chip {...chipSmall} label={`Low Stock: ${listStats.low}`} color="warning" />
                              <Chip {...chipSmall} label={`Party Mismatch: ${listStats.conflict}`} color="error" />
                              <Chip {...chipSmall} label={tab === 3 ? "Mode: PENDING" : "Mode: ALL"} variant="outlined" />
                              <Chip {...chipSmall} label={`Today: ${todayISO()}`} variant="outlined" />
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1 }}>
                          <Box sx={{ height: 520 }}>
                            <DataGrid
                              rows={listRows}
                              columns={listCols}
                              density="compact"
                              rowHeight={30}
                              disableRowSelectionOnClick
                              loading={listLoading}
                              slots={{ toolbar: GridToolbar }}
                              slotProps={{
                                toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } },
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default DpoInsert;
